import { DatePicker, TimePicker } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import moment from "moment";
import { PickerSharedProps } from "rc-picker/es/Picker";
import { PureComponent, ReactNode } from "react";
import ReactDOM from "react-dom";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { ReactSystemFunctions } from "../../../shared/utilty/react-system-functions";
import { CommonProps } from "../common-props";
import { DashboardState } from "../kuika-cl-model-runtimes";

declare let window: any;

enum Displays {
  Date = 1,
  Time = 2,
  DateTime = 3,
  Month = 4
}
interface DateProps extends PickerSharedProps<any> {
  value?: string;
  defaultValue?: string;
  format?: string;
  displayFormat?: string;
  use12Hours?: boolean;
  hourStep?: number;
  minuteStep?: number;
  secondStep?: number;
  onChange?: (value: string | null) => void;
  style?: any;
  placeholder?: string;
  disableBeforeDate?: any;
  disableBeforeTime?: any;
  disableAfterTime?: any;
  className?: string;
  disabledDays?: (
    | "Monday"
    | "Tuesday"
    | "Wednesday"
    | "Thursday"
    | "Friday"
    | "Saturday"
    | "Sunday"
    | "Weekends"
    | "Weekdays"
  )[];
  displayType?: "datetime" | "date" | "time" | "month";
}

interface DateState {
  uniqueKey: Guid;
  isOpen: boolean;
  disableBeforeDate?: any;
}

class KDatePicker extends PureComponent<DateProps & CommonProps, DateState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kdatepicker_${this.uniqueKey}`);

  constructor(props: DateProps) {
    super(props);
    this.state = {
      uniqueKey: Guid.create(),
      isOpen: false,
      disableBeforeDate: undefined
    };
  }

  componentDidMount = () => {
    this.setDynamicStyle();
    if (this.props && this.props.style && this.props.style?.fontSize) {
      const node = ReactDOM.findDOMNode(this) as Element;
      const inputs = node.getElementsByTagName("input");
      if (inputs && inputs.length) {
        for (let index = 0; index < inputs.length; index++) {
          inputs[index].style.fontSize = this.props.style?.fontSize;
        }
      }
    }
  };

  componentDidUpdate(prevProps: DateProps) {
    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  }

  componentWillUnmount = () => {
    this.style.removeStyle();
  };

  getCalculatedDisplay = (): Displays => {
    if (!this.props.format || this.props.format == "") {
      if (this.props.displayType === "datetime") {
        return Displays.DateTime;
      }
      if (this.props.displayType === "date") {
        return Displays.Date;
      }
      if (this.props.displayType === "time") {
        return Displays.Time;
      }
      if (this.props.displayType === "month") {
        return Displays.Month;
      }
      return Displays.Date;
    }
    let calculatedDisplay = Displays.Date;
    switch (this.props.format) {
      case "DD/MM/YYYY":
        calculatedDisplay = Displays.Date;
        break;
      case "MM/DD/YYYY":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY/MM/DD":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY/DD/MM":
        calculatedDisplay = Displays.Date;
        break;
      case "MM/DD":
        calculatedDisplay = Displays.Date;
        break;
      case "D M Y":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY-MM-DD HH:mm":
        calculatedDisplay = Displays.DateTime;
        break;
      case "DD-MM-YYYY HH:mm":
        calculatedDisplay = Displays.DateTime;
        break;
      case "DD-MM-YYYY HH:mm:ss":
        calculatedDisplay = Displays.DateTime;
        break;
      case "mm:ss":
        calculatedDisplay = Displays.Time;
        break;
      case "hh:mm:ss":
        calculatedDisplay = Displays.Time;
        break;
      case "HH:mm":
        calculatedDisplay = Displays.Time;
        break;
      case "YYYY-MM":
        calculatedDisplay = Displays.Month;
        break;
      default:
        calculatedDisplay = Displays.Date;
        break;
    }
    return calculatedDisplay;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  setValue = (value: string | moment.Moment | Date) => {
    if (moment.isMoment(value)) {
      this.handleChange(value);
      return;
    }

    if (value instanceof Date) {
      this.handleChange(moment(value));
    }

    if (typeof value === "string") {
      this.handleChange(moment(value));
    }

    if (value === null || value === undefined) {
      this.handleChange(moment());
    }
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }

    let result = "";
    if (this.props.style?.color || this.props.style?.fontFamily || this.props.style?.fontWeight) {
      result += `
          .${className.trim()} .ant-picker-input>input {
            `;
      if (this.props.style?.color) result += `color: ${this.props.style?.color} !important;`;
      if (this.props.style?.fontFamily) result += `font-family: ${this.props.style?.fontFamily} !important;`;
      if (this.props.style?.fontWeight) result += `font-weight: ${this.props.style?.fontWeight} !important;`;
      result += `;
      }`;
    }

    if (this.props.style?.fontSize || this.props.style?.fontFamily || this.props.style?.fontWeight) {
      result += `
          .${className.trim()} .ant-picker-input>input::-webkit-input-placeholder {
            `;
      if (this.props.style?.fontSize) result += `font-size: ${this.props.style?.fontSize} !important;`;
      if (this.props.style?.fontFamily) result += `font-family: ${this.props.style?.fontFamily} !important;`;
      if (this.props.style?.fontWeight) result += `font-weight: ${this.props.style?.fontWeight} !important;`;
      result += `;
      }`;
    }

    if (this.props.style?.backgroundColor?.length > 0 || this.props.style?.color?.length > 0) {
      result += `
          .${className.trim()} .ant-picker-clear {
            `;
      if (this.props.style?.backgroundColor?.length > 0)
        result += `background: ${this.props.style?.backgroundColor} !important;`;
      if (this.props.style?.color?.length > 0) result += `color: ${this.props.style?.color} !important;`;
      result += `;
      }`;
    }
    return result;
  };

  getClassName = () => {
    let result = "";
    if (this.props.className) {
      result = this.props.className;
    }
    if (!this.state.uniqueKey) {
      return result;
    }
    result = `${result} kdatepicker_${this.state.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  disabledDate = (current) => {
    const cur = _.cloneDeep(current);
    const { disabledDays } = this.props;

    if (cur && disabledDays) {
      const day = cur.day();

      if (
        (disabledDays.includes("Sunday") && day === 0) ||
        (disabledDays.includes("Monday") && day === 1) ||
        (disabledDays.includes("Tuesday") && day === 2) ||
        (disabledDays.includes("Wednesday") && day === 3) ||
        (disabledDays.includes("Thursday") && day === 4) ||
        (disabledDays.includes("Friday") && day === 5) ||
        (disabledDays.includes("Saturday") && day === 6)
      ) {
        return true;
      }

      if (disabledDays.includes("Weekends") && (day === 0 || day === 6)) {
        return true;
      }

      if (disabledDays.includes("Weekdays") && day >= 1 && day <= 5) {
        return true;
      }
    }

    if (this.props.disableBeforeDate || this.state.disableBeforeDate) {
      const value =
        this.state.disableBeforeDate !== undefined
          ? moment(this.state.disableBeforeDate)
          : moment(this.props.disableBeforeDate);
      if (value) {
        if (this.getCalculatedDisplay() != Displays.Date) {
          return cur && cur < moment(value);
        }
        return cur.startOf("day") <= value.startOf("day").add(-1, "day");
      }
    }
    return undefined;
  };

  range = (start: number, end: number) => {
    const result: number[] = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };

  disabledTime = () => {
    const display = this.getDisplayText();

    if (!this.props.disableBeforeTime && !this.props.disableAfterTime) return undefined;
    if (display !== "Date & Time") return undefined;
    const beforeTime = moment(this.props.disableBeforeTime);
    const afterTime = moment(this.props.disableAfterTime);
    if (beforeTime) {
      const disabledBeforeHours = beforeTime.hours();
      const disabledBeforeMinutes = beforeTime.minutes();
      const disabledBeforeSeconds = beforeTime.seconds();

      const disabledAfterHours = afterTime.hours();
      const disabledAfterMinutes = afterTime.minutes();
      const disabledAfterSeconds = afterTime.seconds();

      return {
        disabledHours: () => this.range(0, 24).filter((h) => h < disabledBeforeHours || h > disabledAfterHours),
        disabledMinutes: (selectedHour) => {
          if (selectedHour === disabledBeforeHours) {
            return this.range(0, 60).filter((m) => m < disabledBeforeMinutes);
          }
          if (selectedHour === disabledAfterHours) {
            return this.range(0, 60).filter((m) => m > disabledAfterMinutes);
          }
          return [];
        },
        disabledSeconds: (selectedHour, selectedMinute) => {
          if (selectedHour === disabledBeforeHours && selectedMinute === disabledBeforeMinutes) {
            return this.range(0, 60).filter((s) => s < disabledBeforeSeconds);
          }
          if (selectedHour === disabledAfterHours && selectedMinute === disabledAfterMinutes) {
            return this.range(0, 60).filter((s) => s > disabledAfterSeconds);
          }
          return [];
        }
      };
    }

    return undefined;
  };

  setDisableBeforeDate = (value: any) => {
    if (value) {
      this.setState({
        disableBeforeDate: value
      });
    }
  };

  convertToMoment = (value: string) => {
    if (!value) {
      return null;
    }

    if (typeof value !== "string") {
      return value;
    }

    const format = this.getFormat();
    const isDatetime = this.getCalculatedDisplay() === Displays.DateTime;
    const isTime = this.getCalculatedDisplay() === Displays.Time;

    const useFormat = this.props.format === "hh:mm:ss" && this.props.use12Hours === false ? "HH:mm:ss" : format;

    // Check if the value already contains a timezone indicator
    const hasTimezone = value.includes("Z") || value.includes("+") || value.includes("-");

    // For ISO format strings (common API response format)
    if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(.\d+)?(Z|[+-]\d{2}:?\d{2})?$/)) {
      return moment(value);
    }

    if (isTime) {
      if (/^\d{2}:\d{2}$/.test(value)) {
        const today = moment().format("YYYY-MM-DD");
        return moment(`${today}T${value}:00`, "YYYY-MM-DDTHH:mm:ss");
      }

      if (/^\d{2}:\d{2}:\d{2}$/.test(value)) {
        const today = moment().format("YYYY-MM-DD");
        return moment(`${today}T${value}`, "YYYY-MM-DDTHH:mm:ss");
      }
    }

    const formatsToTry = [
      "YYYY-MM-DDTHH:mm:ss",
      "DD.MM.YYYY HH:mm:ss",
      "D.MM.YYYY HH:mm:ss",
      "DD.M.YYYY HH:mm:ss",
      "D.M.YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm:ss",
      "M/DD/YYYY HH:mm:ss",
      "DD/M/YYYY HH:mm:ss",
      "DD.MM.YYYY HH:mm",
      "D.MM.YYYY HH:mm",
      "MM/DD/YYYY HH:mm",
      "DD/MM/YYYY HH:mm",
      "DD.MM.YYYY",
      "D.MM.YYYY",
      "MM/DD/YYYY",
      "DD/MM/YYYY"
    ];

    // For datetime values, try parsing with UTC preservation first
    if (isDatetime && !hasTimezone) {
      const isoDate = moment.utc(value).format();
      const parsedDate = moment(isoDate);
      if (parsedDate.isValid()) {
        return parsedDate;
      }
    }

    for (const fmt of formatsToTry) {
      const parsedDate = moment(value, fmt);
      if (parsedDate.isValid()) {
        if (isDatetime && !hasTimezone) {
          return moment.utc(parsedDate.format(format));
        }
        return moment(parsedDate.format(format));
      }
    }

    const result2 = isDatetime && !hasTimezone ? moment.utc(value, useFormat) : moment(value, useFormat);
    return result2.isValid() ? result2 : null;
  };

  convertToString = (value: moment.Moment) => {
    if (value && moment.isMoment(value) && value.isValid()) {
      const format = this.getFormat();
      const isTime = this.getCalculatedDisplay() === Displays.Time;

      return value.format(format);
    }
    return null;
  };

  getFormat = () => {
    if (this.getCalculatedDisplay() === Displays.Date) {
      return "YYYY-MM-DD";
    }
    if (this.getCalculatedDisplay() === Displays.Month) {
      return "YYYY-MM";
    }

    return "YYYY-MM-DDTHH:mm:ssZ";
  };

  getDisplayFormat = () => {
    let format: string = this.props.format || "";

    if (format === "hh:mm:ss" && this.props.use12Hours === false) {
      return "HH:mm:ss";
    }

    if (!this.props.format || this.props.format === "") {
      const mlValue = localStorage.getItem("ml") || "";
      const userLocale = mlValue ? mlValue.split("_")[0] : "";
      const displayType = this.props.displayType;

      // Check for app-configured formats first
      const appDateFormat = ReactSystemFunctions.getAppDateFormat();
      const appTimeFormat = ReactSystemFunctions.getAppTimeFormat();

      if (userLocale) {
        if (displayType === "datetime") {
          if (appDateFormat && appTimeFormat) {
            const momentDateFormat = appDateFormat.replace(/dd/g, "DD").replace(/mm/g, "MM").replace(/yyyy/g, "YYYY");
            const momentTimeFormat = appTimeFormat
              .replace(/hh/g, "HH")
              .replace(/mm/g, "mm")
              .replace(/ss/g, "ss")
              .replace(/a/g, "A");
            format = `${momentDateFormat} ${momentTimeFormat}`;
          } else {
            format = `${moment.localeData(userLocale).longDateFormat("L")} ${moment
              .localeData(userLocale)
              .longDateFormat("LT")}`;
          }
        } else if (displayType === "date") {
          if (appDateFormat) {
            format = appDateFormat.replace(/dd/g, "DD").replace(/mm/g, "MM").replace(/yyyy/g, "YYYY");
          } else {
            format = moment.localeData(userLocale).longDateFormat("L");
          }
        } else if (displayType === "time") {
          if (appTimeFormat) {
            format = appTimeFormat.replace(/hh/g, "HH").replace(/mm/g, "mm").replace(/ss/g, "ss").replace(/a/g, "A");
          } else if (this.getCalculatedDisplay() === Displays.Time) {
            if (this.props.format === "mm:ss") {
              format = "mm:ss";
            } else {
              format = this.props.use12Hours ? "hh:mm:ss a" : "HH:mm:ss";
            }
          } else {
            format = this.props.use12Hours ? "hh:mm:ss a" : "HH:mm:ss";
          }
        } else if (displayType === "month") {
          format = moment.localeData(userLocale).longDateFormat("L");
        } else {
          format = "YYYY-MM-DDTHH:mm:ss";
        }
      }
    }

    if (this.props.use12Hours) {
      format = format.replaceAll("HH", "hh");

      if (!format.includes("a") && !format.includes("A")) {
        format += " A";
      }
    } else {
      format = format.replaceAll("hh", "HH");
    }

    return format;
  };

  isDateDisabled = (value: moment.Moment): boolean => {
    if (!value || !value.isValid()) return true;

    const { disabledDays } = this.props;
    if (value && disabledDays) {
      const day = value.day();

      if (
        (disabledDays.includes("Sunday") && day === 0) ||
        (disabledDays.includes("Monday") && day === 1) ||
        (disabledDays.includes("Tuesday") && day === 2) ||
        (disabledDays.includes("Wednesday") && day === 3) ||
        (disabledDays.includes("Thursday") && day === 4) ||
        (disabledDays.includes("Friday") && day === 5) ||
        (disabledDays.includes("Saturday") && day === 6)
      ) {
        return true;
      }

      if (disabledDays.includes("Weekends") && (day === 0 || day === 6)) {
        return true;
      }

      if (disabledDays.includes("Weekdays") && day >= 1 && day <= 5) {
        return true;
      }
    }

    if (this.props.disableBeforeDate || this.state.disableBeforeDate) {
      const disableValue =
        this.state.disableBeforeDate !== undefined
          ? moment(this.state.disableBeforeDate)
          : moment(this.props.disableBeforeDate);
      if (disableValue) {
        if (this.getCalculatedDisplay() != Displays.Date) {
          return value < disableValue;
        }
        return value.startOf("day") <= disableValue.startOf("day").add(-1, "day");
      }
    }
    return false;
  };

  handleChange = (value: any) => {
    if (value === null || value === undefined || value === "") {
      if (this.props.onChange) {
        this.props.onChange(null);
      }
      return;
    }

    try {
      const isDatetime = this.getCalculatedDisplay() === Displays.DateTime;
      const isTime = this.getCalculatedDisplay() === Displays.Time;
      let momentValue;

      if (moment.isMoment(value)) {
        if (isTime) {
          momentValue = value.clone();
        } else if (isDatetime) {
          momentValue = moment(value).local();
        } else {
          momentValue = value;
        }
      } else {
        momentValue = this.convertToMoment(value);
      }

      if (!momentValue || !momentValue.isValid()) {
        if (this.props.onChange) {
          this.props.onChange(null);
        }
        return;
      }

      if (this.isDateDisabled(momentValue)) {
        return;
      }

      const adjustedMomentValue = this.validateTimeSteps(momentValue);
      const dateValue = this.convertToString(adjustedMomentValue);

      if (this.props.onChange) {
        this.props.onChange(dateValue);
      }
    } catch (error) {
      if (this.props.onChange) {
        this.props.onChange(null);
      }
    }
  };

  getGenericValue = () => {
    let result: any = {};
    if (this.props.value) {
      result = { value: this.convertToMoment(this.props.value) };
    }
    return result;
  };

  getGenericDefaultValue = () => {
    let result: any = {};
    if (this.props.defaultValue) {
      if (this.props.defaultValue.trim() == "") {
        return { defaultValue: null };
      }
      result = { defaultValue: this.convertToMoment(this.props.defaultValue.trim()) };
    }
    return result;
  };

  getProps = () => {
    const props: any = _.clone(this.props);
    let style: any = _.clone(this.props.style);
    if (props.defaultValue) {
      delete props.defaultValue;
    }
    if (props.disabledDate) {
      delete props.disabledDate;
    }
    if (props.value) {
      delete props.value;
    }
    if (_.isString(props.value) && props.value.length === 0) {
      delete props.value;
    }
    if (!style) {
      style = {
        width: "100%"
      };
    } else if (!style.width) {
      style.width = "100%";
    }
    if (style.display == "block") {
      style.display = "flex";
    } else if (style.display == "inline") {
      style.display = "inline-flex";
    }
    props.style = style;
    if (this.getCalculatedDisplay() === Displays.DateTime) {
      props.showHour = true;
      props.showMinute = true;
      props.showSecond = true;
    }

    if (this.props.hourStep !== undefined && (this.props.hourStep <= 0 || this.props.hourStep > 24)) {
      delete props.hourStep;
    }

    if (this.props.minuteStep !== undefined && (this.props.minuteStep <= 0 || this.props.minuteStep > 60)) {
      delete props.minuteStep;
    }

    if (this.props.secondStep !== undefined && (this.props.secondStep <= 0 || this.props.secondStep > 60)) {
      delete props.secondStep;
    }

    if (props.format === "hh:mm:ss" && props.use12Hours === false) {
      props.format = "HH:mm:ss";
    }

    return props;
  };

  getDisplayText = () => {
    const display = this.getCalculatedDisplay();
    let displayText = "Date";
    switch (display) {
      case Displays.Date:
        displayText = "Date";
        break;
      case Displays.DateTime:
        displayText = "Date & Time";
        break;
      case Displays.Time:
        displayText = "Time";
        break;
      case Displays.Month:
        displayText = "Month";
        break;
      default:
        displayText = "Date";
        break;
    }
    return displayText;
  };

  validateTimeSteps = (momentValue: moment.Moment): moment.Moment => {
    if (
      !momentValue ||
      !momentValue.isValid() ||
      !(this.props.hourStep || this.props.minuteStep || this.props.secondStep)
    ) {
      return momentValue;
    }

    const display = this.getCalculatedDisplay();
    if (display !== Displays.DateTime && display !== Displays.Time) {
      return momentValue;
    }

    const isTime = display === Displays.Time;

    let adjustedValue = momentValue.clone();

    if (!isTime) {
      const originalOffset = momentValue.utcOffset();
      adjustedValue = adjustedValue.utcOffset(originalOffset);
    }

    if (this.props.hourStep && this.props.hourStep > 0 && this.props.hourStep <= 24) {
      const currentHour = adjustedValue.hour();
      const adjustedHour = Math.round(currentHour / this.props.hourStep) * this.props.hourStep;
      adjustedValue = adjustedValue.hour(Math.min(adjustedHour, 23));
    }

    if (this.props.minuteStep && this.props.minuteStep > 0 && this.props.minuteStep <= 60) {
      const currentMinute = adjustedValue.minute();
      const adjustedMinute = Math.round(currentMinute / this.props.minuteStep) * this.props.minuteStep;
      adjustedValue = adjustedValue.minute(Math.min(adjustedMinute, 59));
    }

    if (this.props.secondStep && this.props.secondStep > 0 && this.props.secondStep <= 60) {
      const currentSecond = adjustedValue.second();
      const adjustedSecond = Math.round(currentSecond / this.props.secondStep) * this.props.secondStep;
      adjustedValue = adjustedValue.second(Math.min(adjustedSecond, 59));
    }

    return adjustedValue;
  };

  handleKeydown = (e: any) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (charCode === 9 && this.state.isOpen) {
      this.setState({ isOpen: false });
      e.preventDefault();
      e.stopPropagation();
      const val = moment(e?.target?.value, this.props.format ?? "");
      if (val.isValid() && !this.isDateDisabled(val)) {
        const adjustedVal = this.validateTimeSteps(val);
        this.handleChange(moment(adjustedVal));
      }
      return false;
    }
    if (charCode === 9 && !this.state.isOpen) {
      if (this.props.onKeyDown) {
        this.props.onKeyDown(e, e.preventDefault());
      }
      return false;
    }
    const { format } = this.props;
    if (
      format !== "YYYY-MM-DD HH:mm:ss" &&
      format !== "DD-MM-YYYY HH:mm:ss" &&
      e.target.value.length === 10 &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186
    ) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (
      charCode > 31 &&
      (charCode > 57 || charCode < 48) &&
      (charCode > 105 || charCode < 96) &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186
    ) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (format === "YYYY/MM/DD" || format === "YYYY/DD/MM" || format === "YYYY-MM-DD HH:mm:ss") {
      this.addSlashToDateInput(e, 4, 7, charCode);
    } else if (format === "DD/MM/YYYY" || format === "MM/DD/YYYY" || format === "DD-MM-YYYY HH:mm:ss") {
      this.addSlashToDateInput(e, 2, 5, charCode);
    } else if (format === "MM/DD") {
      if (e.target.value.length === 5) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addSlashToDateInput(e, 2, undefined, charCode);
    } else if (format === "YYYY/MM") {
      if (e.target.value.length === 7) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addSlashToDateInput(e, 4, undefined, charCode);
    } else if (format === "D M Y") {
      e.target.value = e.target.value;
    } else if (format === undefined) {
      this.addSlashToDateInput(e, 4, 7, charCode);
    }
  };

  addSlashToDateInput = (e: any, firstLength: number, secondLength: number | undefined, charCode: number) => {
    if (e.target.value.length === firstLength && charCode !== 8 && charCode !== 46 && charCode !== 186) {
      e.target.value = `${e.target.value}/`;
    } else if (
      secondLength &&
      e.target.value.length === secondLength &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186
    ) {
      e.target.value = `${e.target.value}/`;
    } else e.target.value = e.target.value.replace(/[^0-9/]/g, "");
  };

  handleOpenChange = (isOpen: boolean) => {
    if (window?.kuika?.dashboardState !== DashboardState.design) {
      this.setState({ isOpen });
    }
  };

  handleBlur = (e: any) => {
    const inputValue = e?.target?.value;
    if (inputValue) {
      const val = moment(inputValue, this.getDisplayFormat());
      if (val.isValid() && !this.isDateDisabled(val)) {
        const adjustedVal = this.validateTimeSteps(val);
        this.handleChange(adjustedVal);
      }
    }
  };

  getLocale = () => {
    return ReactSystemFunctions.getLocale();
  };

  render(): ReactNode {
    const display = this.getDisplayText();
    const isDatetime = this.getCalculatedDisplay() === Displays.DateTime;
    const locale = this.getLocale();

    let timeFormat = this.props.format;
    if (timeFormat === "hh:mm:ss" && this.props.use12Hours === false) {
      timeFormat = "HH:mm:ss";
    }

    return (
      <>
        {(display === "Date" || display === "Date & Time" || display === "Month") && (
          <DatePicker
            open={this.state.isOpen}
            onOpenChange={this.handleOpenChange}
            className={this.getClassName()}
            disabledDate={this.disabledDate}
            disabledTime={this.disabledTime}
            locale={locale}
            {...this.getProps()}
            showTime={
              display === "Date & Time" || this.props.displayType === "datetime"
                ? {
                    defaultValue: moment("00:00:00", "HH:mm:ss"),
                    utcOffset: isDatetime ? moment().utcOffset() : undefined
                  }
                : false
            }
            picker={display === "Month" ? "month" : "date"}
            {...this.getGenericValue()}
            {...this.getGenericDefaultValue()}
            onChange={this.handleChange}
            onKeyDown={(e) => this.handleKeydown(e)}
            format={this.getDisplayFormat()}
          ></DatePicker>
        )}
        {display === "Time" && (
          <TimePicker
            open={this.state.isOpen}
            onOpenChange={this.handleOpenChange}
            className={this.getClassName()}
            locale={locale}
            {...this.getProps()}
            {...this.getGenericValue()}
            {...this.getGenericDefaultValue()}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            format={this.getDisplayFormat()}
          ></TimePicker>
        )}
      </>
    );
  }
}
const kDatePicker = withCommonEvents(KDatePicker);
export { kDatePicker as KDatePicker };
